import _extends from "@babel/runtime/helpers/extends";
import _initializerDefineProperty from "@babel/runtime/helpers/initializerDefineProperty";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _applyDecoratedDescriptor from "@babel/runtime/helpers/applyDecoratedDescriptor";
import _initializerWarningHelper from "@babel/runtime/helpers/initializerWarningHelper";
var _class, _class2, _descriptor, _descriptor2, _descriptor3, _descriptor4, _descriptor5, _descriptor6, _descriptor7, _descriptor8, _AbPopup;
/*
 *                     NOTICE
 *            COPYRIGHT 2018 Ab Initio
 *       UNPUBLISHED -- ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { AbErrorBoundary } from "abreact/incubator/ui/component/AbErrorBoundary";
import { BaseRCmp } from "abreact/incubator/ui/component/BaseRCmp";
import { LayerManager } from "abreact/incubator/ui/manager/LayerManager";
import { AbOverlay } from "abreact/incubator/ui/popup/AbOverlay";
import { AbPopupPortal } from "abreact/incubator/ui/popup/AbPopupPortal";
import { AbPopupContext, ExplicitlyShownPopupContext, LayerGroupContext, ModalPopupContext } from "abreact/incubator/ui/popup/PopupContext";
import { PopupPosition } from "abreact/incubator/ui/popup/PopupPosition";
import { PopupHandle, PopupUtils } from "abreact/incubator/ui/popup/PopupUtils";
import { FocusUtils } from "abreact/incubator/ui/utils/FocusUtils";
import { ReactUtils } from "abreact/incubator/ui/utils/ReactUtils";
import { AbResizeBox } from "abreact/layout/AbResizeBox";
import { AbHeaderRootContext } from "abreact/misc/AbHeader";
import { AbSizeContext } from "abreact/misc/AbSize";
import { AbWithTooltip } from "abreact/popup/AbWithTooltip";
import { useWindowSize } from "abreact/utils/AbHooks";
import { AbSet } from "abweb/collection/AbSet";
import { LocaleManager } from "abweb/service/locale/LocaleManager";
import { ClassUtils } from "abweb/utils/ClassUtils";
import { KeyValue } from "abweb/utils/KeyboardUtils";
import { NumberUtils } from "abweb/utils/NumberUtils";
import { AbResizeObserver } from "abweb/utils/geometry/AbResizeObserver";
import { LogUtils } from "abweb/utils/log/LogUtils";
import { action, makeObservable, observable, runInAction } from "mobx";
import { observer } from "mobx-react";
import * as React from "react";
import Draggable from "react-draggable";
const LOGGER = LogUtils.getLogger(__filename);
// Same as default dialog header height
const MINIMUM_GRAB_TARGET_SIZE = 50;
const calcAllowPropagation = (allowPropagation, key) => {
  if (allowPropagation === true) return true;
  if (!allowPropagation) return false;
  if (allowPropagation[key]) return true;
  return false;
};

/**
 * A simple popup window class.
 */
let AbPopup = observer(_class = (_class2 = (_AbPopup = class AbPopup extends BaseRCmp {
  get layerGroup() {
    return LayerManager.getOptionalLayerGroup(this.layerGroupId);
  }
  get ownerDocument() {
    return this.layerGroup?.layerRootElement.ownerDocument ?? document;
  }
  constructor(props) {
    super(props);
    _initializerDefineProperty(this, "userResizedPopup", _descriptor, this);
    _initializerDefineProperty(this, "userDraggedPopup", _descriptor2, this);
    _initializerDefineProperty(this, "resizeDirectionsToDisable", _descriptor3, this);
    _initializerDefineProperty(this, "resizeBoxOnlyManageSize", _descriptor4, this);
    _initializerDefineProperty(this, "positionerStyling", _descriptor5, this);
    _initializerDefineProperty(this, "draggableBounds", _descriptor6, this);
    _initializerDefineProperty(this, "draggableGoToPosition", _descriptor7, this);
    _defineProperty(this, "resizeHappening", false);
    _defineProperty(this, "dragCurrentTranslation", {
      x: 0,
      y: 0
    });
    _defineProperty(this, "popupElement", void 0);
    _defineProperty(this, "origin", void 0);
    _defineProperty(this, "previousActiveElement", void 0);
    _defineProperty(this, "layerGroupId", void 0);
    _defineProperty(this, "resizeObserver", new AbResizeObserver((_, element) => {
      this.updatePositionAndOrientation(element);
    }));
    _defineProperty(this, "onRemoveModalPopup", void 0);
    _defineProperty(this, "getTabbableElements", () => {
      if (!this.popupElement) return undefined;
      return PopupUtils.getTabbableElements(this.popupElement);
    });
    /**
     * This handler is called when the user clicks on the background behind a dialog.
     *
     * @param {MouseEvent} event The mouse event that was fired.
     */
    _initializerDefineProperty(this, "onBackgroundCaptureMouseDown", _descriptor8, this);
    _defineProperty(this, "onBackgroundClick", event => {
      this.props.onBackgroundClick?.(event);
    });
    _defineProperty(this, "onDrag", (e, data) => {
      this.dragCurrentTranslation = {
        x: data.x,
        y: data.y
      };
    });
    // Fix for React-Draggable not working with nested popups
    // See https://github.com/mzabriskie/react-draggable/issues/11
    _defineProperty(this, "onDragStart", e => {
      e.stopPropagation();
      runInAction(() => this.draggableGoToPosition = undefined);
    });
    _defineProperty(this, "onDragEnd", () => {
      if (!this.userDraggedPopup) {
        runInAction(() => {
          if (this.isAutoCentered && this.popupElement) {
            this.draggableGoToPosition = {
              x: this.popupElement.offsetLeft + this.dragCurrentTranslation.x,
              y: this.popupElement.offsetTop + this.dragCurrentTranslation.y
            };
            this.dragCurrentTranslation = this.draggableGoToPosition;
          }
          this.userDraggedPopup = true;
        });
      }
    });
    _defineProperty(this, "handleScroll", () => {
      if (this.props.closeOnBackgroundScroll)
        //TODO only if target isn't an input
        {
          this.closePopup();
          return;
        }
      if (this.popupElement && this.layerGroup) {
        this.updatePositionAndOrientation(this.popupElement);
      }
    });
    _defineProperty(this, "calcBounds", () => {
      if (!this.popupElement) return;
      const safeBounds = {
        top: -this.popupElement.offsetTop + PopupUtils.POSITIONER_DEFAULT_WINDOW_MARGIN,
        bottom: -this.popupElement.offsetTop + this.props.windowHeight - MINIMUM_GRAB_TARGET_SIZE,
        left: -this.popupElement.offsetLeft - this.popupElement.offsetWidth + MINIMUM_GRAB_TARGET_SIZE,
        right: -this.popupElement.offsetLeft + this.props.windowWidth - MINIMUM_GRAB_TARGET_SIZE
      };
      if (this.draggableBounds && NumberUtils.fuzzyEquals(safeBounds.top, this.draggableBounds.top, 1) && NumberUtils.fuzzyEquals(safeBounds.right, this.draggableBounds.right, 1) && NumberUtils.fuzzyEquals(safeBounds.bottom, this.draggableBounds.bottom, 1) && NumberUtils.fuzzyEquals(safeBounds.left, this.draggableBounds.left, 1)) {
        return;
      }
      runInAction(() => this.draggableBounds = safeBounds);
    });
    _defineProperty(this, "updated", false);
    _defineProperty(this, "keepPopupOnScreen", popupElement => {
      const topPosition = this.dragCurrentTranslation.y + popupElement.offsetTop;
      const leftPosition = this.dragCurrentTranslation.x + popupElement.offsetLeft;
      const minTop = PopupUtils.POSITIONER_DEFAULT_WINDOW_MARGIN;
      const maxTop = this.props.windowHeight - MINIMUM_GRAB_TARGET_SIZE;
      const minLeft = -popupElement.offsetWidth + MINIMUM_GRAB_TARGET_SIZE;
      const maxLeft = this.props.windowWidth - MINIMUM_GRAB_TARGET_SIZE;
      if (!NumberUtils.inRange(topPosition, minTop, maxTop) || !NumberUtils.inRange(leftPosition, minLeft, maxLeft)) {
        runInAction(() => {
          const goalLeft = NumberUtils.clamp(leftPosition, minLeft, maxLeft);
          const goalTop = NumberUtils.clamp(topPosition, minTop, maxTop);
          const deltaLeft = goalLeft - leftPosition;
          const deltaTop = goalTop - topPosition;
          const newDragPosition = {
            x: this.dragCurrentTranslation.x + deltaLeft,
            y: this.dragCurrentTranslation.y + deltaTop
          };
          this.draggableGoToPosition = newDragPosition;
          this.dragCurrentTranslation = newDragPosition;
        });
      }
    });
    this.userResizedPopup = false;
    this.userDraggedPopup = false;
    this.layerGroupId = props.layerGroupId;
    if (!props.popupHandle) {
      throw new Error("Popup cannot be constructed without a popup handle in its context");
    }
    if (ClassUtils.isInstanceOf(this.ownerDocument.activeElement, HTMLElement)) {
      this.previousActiveElement = this.ownerDocument.activeElement;
    }
    this.origin = this.props.origin ?? this.previousActiveElement;
    // Plug memory leak: when component is unmounted, forget the origin.
    this.registerDestroyListener(() => this.origin = undefined);
    makeObservable(this);
  }
  get popupHandle() {
    const popupHandle = this.props.popupHandle;
    if (ClassUtils.isInstanceOf(popupHandle, PopupHandle)) {
      return popupHandle;
    }
    throw new Error(`Cannot determine popup handle for ${popupHandle}`);
  }

  /**
   * Returns true if the popup is currently topmost.
   */
  isTopmost() {
    return this.popupHandle.id === LayerManager.UNSAFE_topmostLayer?.id;
  }

  /**
   * Returns the position of the popup.
   */
  get position() {
    if (this.props.position !== undefined) {
      return this.props.position;
    } else {
      return this.userResizedPopup || this.userDraggedPopup || !this.showOverlay ? PopupPosition.FIXED : PopupPosition.CENTER;
    }
  }

  /**
   * Returns true if this popup is modal.
   */
  get modal() {
    return this.props.modal ?? (this.props.overlay !== false || this.props.closeOnBackgroundClick === true);
  }

  /**
   * Returns true if this popup is modeless.
   *
   * @deprecated in favor of AbPopup.modal
   */
  get modeless() {
    return !this.modal;
  }

  /**
   * Returns true if this popup should be shown with an overlay.
   */
  get showOverlay() {
    return this.props.overlay ?? this.props.modal ?? true;
  }
  componentDidMount() {
    const layerGroup = this.layerGroup;
    const id = PopupUtils.getPopupId(this.popupHandle);
    if (this.showOverlay && layerGroup && id) {
      this.onRemoveModalPopup = LayerManager.addModalPopup(layerGroup.appRootElement, id);
    }
    // Clear tooltip timeout
    if (!this.props.className?.split(" ").includes("ab-tooltip")) {
      AbWithTooltip.__clearEnterTimeout();
    }
    // Register this with the master list of all popups.
    AbPopup.allPopups.push(this);
    this.layerGroup?.registerEventHandlers(this.popupHandle.layer, {
      onBackgroundScroll: this.handleScroll,
      onBackgroundClick: this.onBackgroundClick,
      onBackgroundCaptureMouseDown: this.onBackgroundCaptureMouseDown
    });
    if (this.props.focus) {
      this.setFocus();
      // For modal popups, add a trap to keep focus within the popup
      if (this.modal) {
        this.onFocusInHandler = event => this.onFocusIn(event);
        this.ownerDocument.body.addEventListener("focusin", this.onFocusInHandler);
      }
    }
    if (!this.popupElement) return;
    // Record the initial width & height so the resize detector doesn't go off
    const popupWidth = this.popupElement.offsetWidth;
    const popupHeight = this.popupElement.offsetHeight;

    /* This block of code is for the case where authors want the popup to be in the middle of the screen, but
       NOT subject to the way "autocentering" works (i.e. all 4 corners move when you resize).  To request such a
       popup, the author sets position to "FIXED", and does not specify a position through the style.
       If, on the other hand, the author wants the "autocentering" position, they set position to "Center",
       which is the default.
     */
    const autoGenLeftAndTop = this.props.style === undefined || this.props.style.left === undefined && this.props.style.top === undefined && this.props.style.right === undefined && this.props.style.bottom === undefined;
    if (this.position === PopupPosition.FIXED && autoGenLeftAndTop && !this.props.positioner) {
      const autoLeft = (this.props.windowWidth - popupWidth) / 2;
      const autoTop = (this.props.windowHeight - popupHeight) / 2;
      this.popupElement.style.left = autoLeft + "px";
      this.popupElement.style.top = autoTop + "px";
    }
  }
  componentDidUpdate(prevProps) {
    if (prevProps.windowHeight !== this.props.windowHeight || prevProps.windowWidth !== this.props.windowWidth) {
      this.popupElement && this.updatePositionAndOrientation(this.popupElement);
    }
  }
  componentWillUnmount() {
    super.componentWillUnmount();

    // Remove this popup from the master list of all popups
    const index = AbPopup.allPopups.indexOf(this);
    if (index > 0) {
      AbPopup.allPopups.splice(index, 1);
    }

    // If this popup took the focus initially and focus is in the popup, restore it back to where it was
    const previousActiveElement = this.previousActiveElement;
    const ownerDocument = this.ownerDocument;
    // Only set focus if focus is inside the popup or nowhere in particular.
    const shouldSetFocus = this.popupElement?.contains(ownerDocument.activeElement) || ownerDocument.activeElement === ownerDocument.body;
    if (this.props.focus && this.modal && previousActiveElement && shouldSetFocus) {
      // Slow down this focus call
      window.requestAnimationFrame(() => {
        if (ownerDocument.activeElement === undefined || ownerDocument.activeElement === ownerDocument.body) {
          previousActiveElement?.focus();
        }
      });
    }
    this.popupElement = undefined;
    this.resizeObserver.disconnect();
    this.layerGroup?.unregisterEventHandlers(this.popupHandle.layer);
    if (this.onFocusInHandler) {
      this.ownerDocument.body.removeEventListener("focusin", this.onFocusInHandler);
    }
    this.props.onClose && this.props.explicitlyShown && this.props.onClose();
    this.onRemoveModalPopup?.();
  }
  setFocus() {
    if (this.props.setFocus) {
      this.props.setFocus();
    } else {
      const initialFocusElement = this.getInitialFocusElement();
      initialFocusElement && FocusUtils.setFocus(initialFocusElement);
    }
  }
  getInitialFocusElement() {
    // If popup element does not exist or already has focus set within it, do nothing.
    if (!this.popupElement) return undefined;
    if (this.popupElement.querySelector(":focus")) return undefined;
    return findFirstPopupTabbableElement(this.popupElement);
  }
  closePopup() {
    this.props.popupHandle.close();
  }
  onFocusIn(event) {
    const target = event.target;
    const popupElement = this.popupElement;
    // If this is the topmost React popup, and the focus is being sent outside then bring it back.
    // Note: don't grab focus away from Kendo popups that are shown above the React dialog.
    if (this.isTopmost() && popupElement && ClassUtils.isInstanceOf(target, HTMLElement)) {
      if (!AbPopup.isInKendoPopup(target) && !popupElement.contains(target)) {
        this.setFocus();
      }
    }
  }

  /**
   * Returns true if the specified element is showing in a Kendo popup.
   * @param element
   */
  static isInKendoPopup(element) {
    return element.closest(".k-window") != null;
  }
  onKeyPress(event) {
    if (!calcAllowPropagation(this.props.allowPropagation, "onKeyPress")) {
      event.stopPropagation();
    }
    if (this.props.onKeyPress?.(event)) {
      event.preventDefault();
      return;
    }
  }
  onKeyDown(event) {
    if (!calcAllowPropagation(this.props.allowPropagation, "onKeyDown")) {
      event.stopPropagation();
    }
    if (this.props.onKeyDown?.(event)) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    if (event.key === KeyValue.ESCAPE) {
      event.preventDefault();
      event.stopPropagation();
      this.closePopup();
    }
  }
  onKeyUp(event) {
    if (!calcAllowPropagation(this.props.allowPropagation, "onKeyUp")) {
      event.stopPropagation();
    }
    if (this.props.onKeyUp?.(event)) {
      event.preventDefault();
      event.stopPropagation();
    }
  }
  onClick(event) {
    if (!calcAllowPropagation(this.props.allowPropagation, "onClick")) {
      event.stopPropagation();
    }
    this.props.onClick?.(event);
  }
  onMouseEnter(event) {
    if (!calcAllowPropagation(this.props.allowPropagation, "onMouseEnter")) {
      event.stopPropagation();
    }
    this.props.onMouseEnter?.(event);
  }
  onMouseLeave(event) {
    if (!calcAllowPropagation(this.props.allowPropagation, "onMouseLeave")) {
      event.stopPropagation();
    }
    this.props.onMouseLeave?.(event);
  }
  onClickOverlay(event) {
    let target = event.target;
    // Recursively close layers until we find a non-layer element at the click point
    while (target && target.classList.contains("ab-overlay") && target.classList.contains("close-on-click")) {
      // Close the dialog by removing it from the layer manager.
      this.closePopup();

      // Hide the layer immediately so that we can find the element beneath it
      target.style.display = "none";

      // Determine the element remaining at the same point
      let newTarget = this.ownerDocument.elementFromPoint(event.pageX, event.pageY);

      // Don't keep recursing if for some reason the new target is the same as the old one
      target = newTarget !== target ? newTarget : null;
    }
    // Give focus back to the popup (will do nothing if the popup was closed)
    this.setFocus();
  }
  get isAutoCentered() {
    // A dialog is auto-centered if the position is centered, the dialog has not been resized,
    // and no positioner is provided
    return this.showOverlay && this.position === PopupPosition.CENTER && !this.userResizedPopup && !this.userDraggedPopup && !this.props.positioner;
  }
  render() {
    // TODO - change tests currently using ab-qa-popup-qa
    //  to instead use [data-test-component='ab-popup'] or [data-test-id='whatever']
    //  and then remove classname ab-qa-popup-qa
    const classes = ReactUtils.componentClassNamesString("ab-popup ab-qa-popup-qa", this.props, [this.modal && "ab-modal-popup", this.props.resizable && !this.props.isMaximized ? "ab-popup-is-resizable" : undefined, this.userResizedPopup && "ab-popup-was-resized", this.props.isMaximized && "ab-popup-is-maximized", LocaleManager.localeCSSClassName]);
    const popupHandle = this.popupHandle;
    const blockPropagation = calcBlockPropagation(this.props.allowPropagation);
    const fixedStyle = this.isAutoCentered ? {} : {
      position: "fixed",
      opacity: this.positionerStyling ? undefined : 0
    };
    const style = {
      ...fixedStyle,
      ...this.props.style,
      ...this.positionerStyling
    };

    // Note: don't even render a popup if it has no children. This is useful for popups
    // that start out hidden but then add their contents at a later date.
    let popup = this.props.children && /*#__PURE__*/React.createElement(AbResizeBox, _extends({}, this.props.__htmlProps, {
      position: "absolute",
      directionsToDisable: this.resizeDirectionsToDisable,
      onlyManageSize: this.resizeBoxOnlyManageSize,
      onResizeStart: () => {
        this.resizeHappening = true;
      },
      onResizeEnd: () => this.resizeHappening = false,
      onResize: () => {
        this.props.onResized?.();
        this.calcBounds();
        if (!this.userResizedPopup) runInAction(() => this.userResizedPopup = true);
      },
      disabled: !this.props.resizable || this.props.isMaximized,
      id: PopupUtils.getPopupId(popupHandle),
      className: classes,
      role: this.props.role,
      "aria-labelledby": this.props.ariaLabelledByID
    }, blockPropagation, {
      onKeyDown: this.onKeyDown.bind(this),
      onKeyUp: this.onKeyUp.bind(this),
      onKeyPress: this.onKeyPress.bind(this),
      onMouseEnter: this.onMouseEnter.bind(this),
      onMouseLeave: this.onMouseLeave.bind(this),
      onClick: this.onClick.bind(this),
      tabIndex: -1,
      style: style,
      abRef: this.props.resizeBoxRef,
      ref: div => {
        this.popupElement = div ?? undefined;
        if (!div) return;
        this.calcBounds();
        this.resizeObserver.observe(div);
        if (this.props.stayInInitialPosition && !this.updated) {
          this.updatePositionAndOrientation(div);
          this.updated = true;
        }
      },
      "data-test-component": "ab-popup",
      "data-test-id": this.props.dataTestId
    }), (this.modal || this.props.closeOnTabOut) && /*#__PURE__*/React.createElement("div", {
      tabIndex: 0,
      className: "ab-popup-focus-trap",
      onFocus: () => {
        if (this.props.closeOnTabOut) this.closePopup();
        this.getTabbableElements()?.last()?.focus();
      }
    }), /*#__PURE__*/React.createElement(ModalPopupContext.Provider, {
      value: this.modal
    }, this.props.flushContent ? this.props.children : /*#__PURE__*/React.createElement("div", {
      className: "ab-popup-content"
    }, this.props.children)), (this.modal || this.props.closeOnTabOut) && /*#__PURE__*/React.createElement("div", {
      tabIndex: 0,
      className: "ab-popup-focus-trap",
      onFocus: () => {
        if (this.props.closeOnTabOut) this.closePopup();
        this.getTabbableElements()?.first()?.focus();
      }
    }));
    if (this.props.draggable) {
      popup = /*#__PURE__*/React.createElement(Draggable, {
        handle: this.props.draggableElementSelector ?? ".ab-popup-content",
        onStart: this.onDragStart,
        onDrag: this.onDrag,
        onStop: this.onDragEnd,
        onMouseDown: e => blockPropagation.onMouseDown?.(e),
        bounds: this.draggableBounds,
        position: this.draggableGoToPosition,
        disabled: this.props.isMaximized
      }, popup);
    }
    if (this.showOverlay) {
      const overlayClassnames = ReactUtils.classNamesString([this.props.overlayClassnames, this.props.closeOnBackgroundClick && "close-on-click"]);
      popup = /*#__PURE__*/React.createElement(AbOverlay, {
        className: overlayClassnames,
        onClick: this.onClickOverlay.bind(this),
        popupCentered: this.isAutoCentered,
        transparent: this.props.transparentOverlay
      }, popup);
    }
    return /*#__PURE__*/React.createElement(AbErrorBoundary, {
      onError: () => this.closePopup()
    }, popup);
  }

  /**
   * Returns the positioner to use for the current dialog.
   * @returns {PopupPositioner | undefined}
   */
  get positioner() {
    if (this.props.positioner) return this.props.positioner;
    return PopupUtils.defaultPositioner(this.position);
  }
  /* Window dimensions are passed for the re-rendering triggered by MobX to work */
  updatePositionAndOrientation(popupElement) {
    if (this.resizeHappening) return;
    this.keepPopupOnScreen(popupElement);
    const positioner = this.positioner;
    if (positioner && this.origin) {
      // Invoke the positioner to update the dialog element's position
      const {
        resizeDirectionsToDisable,
        resizeBoxOnlyManageSize,
        popupPosition,
        ...rest
      } = positioner({
        popupHandle: this.popupHandle,
        popupElement: popupElement,
        relativeToClientRect: this.origin.getBoundingClientRect(),
        position: this.position
      });
      // Filter out undefined values
      Object.keys(rest).forEach(key => key in rest && rest[key] === undefined && delete rest[key]);
      runInAction(() => {
        this.resizeDirectionsToDisable = resizeDirectionsToDisable;
        this.resizeBoxOnlyManageSize = resizeBoxOnlyManageSize;
        // Add margin unset to defeat AbPopup default styling
        this.positionerStyling = {
          ...rest,
          margin: "unset"
        };
      });
    } else {
      runInAction(() => this.positionerStyling = {});
    }
  }
}, _defineProperty(_AbPopup, "allPopups", []), _AbPopup), _descriptor = _applyDecoratedDescriptor(_class2.prototype, "userResizedPopup", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class2.prototype, "userDraggedPopup", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor3 = _applyDecoratedDescriptor(_class2.prototype, "resizeDirectionsToDisable", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor4 = _applyDecoratedDescriptor(_class2.prototype, "resizeBoxOnlyManageSize", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor5 = _applyDecoratedDescriptor(_class2.prototype, "positionerStyling", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor6 = _applyDecoratedDescriptor(_class2.prototype, "draggableBounds", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor7 = _applyDecoratedDescriptor(_class2.prototype, "draggableGoToPosition", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class2.prototype, "closePopup", [action], Object.getOwnPropertyDescriptor(_class2.prototype, "closePopup"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "onClickOverlay", [action], Object.getOwnPropertyDescriptor(_class2.prototype, "onClickOverlay"), _class2.prototype), _descriptor8 = _applyDecoratedDescriptor(_class2.prototype, "onBackgroundCaptureMouseDown", [action], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return event => {
      const bubbleBackgroundCaptureMouseDown = this.props.onBackgroundCaptureMouseDown?.(event) === true;
      if (this.props.closeOnBackgroundClick) {
        this.closePopup();
        return true;
      }
      // Prevent popups behind me from closing unless bubbleBackgroundCaptureMouseDown is true
      return bubbleBackgroundCaptureMouseDown;
    };
  }
}), _class2)) || _class;
const AbPopupContextWrapper = props => {
  const popupContext = React.useContext(AbPopupContext);
  if (!popupContext) throw new Error("popup cannot render without popup context");
  const layerGroupContext = React.useContext(LayerGroupContext);
  const layerGroupId = layerGroupContext ?? LayerManager.DEFAULT_GROUP_ID;
  const view = LayerManager.getOptionalLayerGroup(layerGroupId)?.layerRootElement.ownerDocument.defaultView;
  const explicitlyShownContext = React.useContext(ExplicitlyShownPopupContext);
  const {
    windowWidth,
    windowHeight
  } = useWindowSize(60, view ?? undefined);
  // Set undefined size provider to match root of dom
  return /*#__PURE__*/React.createElement(AbSizeContext.Provider, {
    value: undefined
  }, /*#__PURE__*/React.createElement(AbHeaderRootContext, null, /*#__PURE__*/React.createElement(AbPopup, _extends({}, props, {
    popupHandle: popupContext.popupHandle,
    layerGroupId: layerGroupId,
    explicitlyShown: explicitlyShownContext,
    windowWidth: windowWidth,
    windowHeight: windowHeight
  }))));
};
const AbPopupDeclarativeWrapper = props => {
  const explicitlyShownContext = React.useContext(ExplicitlyShownPopupContext);
  if (explicitlyShownContext) {
    // Note: provide explicitlyShown: false to child nodes so that they can
    // provide declarative popups if desired.
    return /*#__PURE__*/React.createElement(AbPopupContextWrapper, props, /*#__PURE__*/React.createElement(ExplicitlyShownPopupContext.Provider, {
      value: undefined
    }, props.children));
  } else {
    if (!props.onClose) {
      // TODO reinstate error
      LOGGER.error("Declarative popups must have a provided onClose method which unmounts the popup.");
      // throw new Error("Declarative popups must have a provided onClose method which unmounts the popup.")
    }
    return /*#__PURE__*/React.createElement(AbPopupPortal, {
      popupId: props.id,
      close: props.onClose,
      ownerPopupRegistry: props.ownerPopupRegistry
    }, /*#__PURE__*/React.createElement(AbPopupContextWrapper, props, props.children));
  }
};
export { AbPopupDeclarativeWrapper as AbPopup };

/**
 * mproteau: I threw this function together because of the following convoluted story:
 * Back in days when Mhub used a Gelato-based application (VdlApplication) it relied on some pretty heavy-handed code
 * to destroy rendered stuff between view transitions. In modern (Portal-esque) applications, the onus is on the view
 * to clean up its popups when unmounted. Unfortunately, a lot of code uses AbPopupManager.show() and does nothing with
 * the returned handle. The result is the popups never closing when using browser navigation or changing the browser
 * url. To work around this, I put in this function to make it so view transitions
 */
export function forceModalPopupsClosed() {
  Array.from(AbPopup.allPopups).reverse().forEach(popup => {
    if (popup.props.modal) {
      popup.props.popupHandle.close();
    }
  });
}

/**
 * Closes all popups that are transient, i.e. which have "closeOnBackgroundClick" true.
 *
 * Note: use this function carefully as it can end up closing popups that you wouldn't expect.
 * It is better to keep track of your popups and then close them explicitly as needed.
 */
export function closeAllTransientPopups() {
  // Iterate the popups from front to back to close them in that order
  Array.from(AbPopup.allPopups).reverse().forEach(popup => {
    if (popup.props.closeOnBackgroundClick) {
      popup.props.popupHandle.close();
    }
  });
}
export function closeAllTransientPopupsWithClassname(className) {
  // Iterate the popups from front to back to close them in that order
  Array.from(AbPopup.allPopups).reverse().forEach(popup => {
    if (popup.props.closeOnBackgroundClick && popup.props.className?.split(" ").includes(className)) {
      popup.props.popupHandle.close();
    }
  });
}

// Utility for finding first element that ought to be focused in a popup
const findFirstPopupTabbableElement = root => {
  // Look for first tabbable item in dialog content
  const dialogContent = root.querySelector(".ab-dialog-content");
  if (dialogContent) {
    const dialogContentTabbable = PopupUtils.getTabbableElements(dialogContent);
    const firstMatch = dialogContentTabbable.first();
    if (firstMatch) {
      return firstMatch;
    }
  }
  // Look for optimal button in dialog footer
  const dialogFooter = root.querySelector(".ab-dialog-footer");
  if (dialogFooter) {
    const bestFooterButton = PopupUtils.findFirstTabbableElement(dialogFooter, [".ab-button-submit", ".ab-button-primary", ".ab-dialog-cancel-button"]);
    if (bestFooterButton) return bestFooterButton;
  }
  // Look for first tabbable item not in a popup header
  const allTabbable = PopupUtils.getTabbableElements(root);
  const header = root.querySelector(".ab-popup-header");
  if (header) {
    const headerTabbable = AbSet.from(PopupUtils.getTabbableElements(header));
    for (const element of allTabbable) {
      if (!headerTabbable.has(element)) {
        return element;
      }
    }
  }
  // Otherwise, return the first tabbable thing, defaulting to the popup itself.
  return allTabbable.first() ?? root;
};

// Some events need to stop, but go to the top-level document anyway
const forward = e => {
  e.stopPropagation();
  const ownerDocument = e.target.ownerDocument;
  // clone the native event so it can be re-dispatched
  /* eslint-disable-next-line abinitio/no-instanceof */
  const eventClone = e.nativeEvent instanceof ownerDocument.defaultView?.Event ? new e.nativeEvent.__proto__.constructor(e.type, e.nativeEvent) : new Event(e.type, e.nativeEvent);
  ownerDocument.dispatchEvent(eventClone);
};
const stop = e => e.stopPropagation();
const blockPropagation = {
  onClick: stop,
  onContextMenu: stop,
  onDoubleClick: stop,
  onDrag: forward,
  onDragEnd: forward,
  onDragEnter: forward,
  onDragExit: forward,
  onDragLeave: forward,
  onDragOver: forward,
  onDragStart: forward,
  onDrop: forward,
  onMouseDown: stop,
  onMouseEnter: forward,
  onMouseLeave: forward,
  onMouseMove: forward,
  onMouseOver: forward,
  onMouseOut: forward,
  onMouseUp: forward,
  onKeyDown: stop,
  onKeyPress: stop,
  onKeyUp: stop,
  // TODO: Probably needs to be revisited. Commented out for Firefox
  //onFocus: stop,
  //onBlur: stop,

  onChange: stop,
  onInput: stop,
  onInvalid: stop,
  onSubmit: stop
};
const calcBlockPropagation = allowPropagation => {
  return Object.fromEntries(Object.entries(blockPropagation).filter(([key, value]) => {
    return !calcAllowPropagation(allowPropagation, key);
  }));
};
//# sourceMappingURL=AbPopup.js.map
